<!--
  ApprovalWorkspacePage.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Route shell for the Approval Workspace, mounted at both
  ``/distributor/decorations/approvals`` (list-only) and
  ``/distributor/decorations/approvals/:approvalId`` (with detail open).
  The deep-link route is per P2-Q3 so a Builder can pass a URL to a
  teammate that opens straight onto a record.

  Layout (two-column under the workspace header):

    +---------------------------------------------------------------+
    |  H1 Approvals                                                 |
    |  Summary cards                                                |
    |  Filter chips                                                 |
    +---------------------------------+-----------------------------+
    |  Queue (left, ~360px)           |  Detail panel (right, fill) |
    |  - grouped by client            |                             |
    |  - selected row highlighted     |                             |
    +---------------------------------+-----------------------------+

  Route-driven detail selection: when ``:approvalId`` is present we
  open it; clicking a queue row pushes the URL so back/forward and
  link-sharing both work. The page is responsible for converting URL
  param <-> store selection (the rest of the shell is presentation).

  Capability gating is handled by the route definition's
  ``beforeEnter: requireCapability('distributor.decoration_library.read')``
  guard; this component assumes the user already has at least read
  capability. The action surfaces inside the detail panel are gated on
  ``distributor.decoration_library.write``.

  P5 vocabulary note
  ------------------
  Per ``docs/integrations/decoration/vocabulary.md`` row "Approval
  Workspace", the user-facing noun on this surface stays "Decoration"
  -- the matrix axis (Decoration x Client) didn't change shape in the
  refactor, only the underlying row (the data backing each Decoration
  is now an artwork + method + dimensions tuple instead of a
  DecorationArtifact). The Approval Workspace data source (the
  approval_records endpoint) is already correctly keyed via P3 PR-A's
  additive re-key (artwork_version_id + method_id + dimensions +
  client_id), so this page reads the post-refactor shape transparently
  with no client-side change.
-->
<template>
    <div class="aw-page" data-testid="approval-workspace-page">
        <header class="aw-page__header">
            <div>
                <h1 class="aw-page__title">Approvals</h1>
                <p class="aw-page__sub">
                    Triage every artwork awaiting a client decision,
                    digitizer round-trip, or builder follow-up.
                </p>
            </div>
        </header>

        <ApprovalSummaryCards
            :counts="store.counts"
            @update:filter="onFilterChange"
        />
        <ApprovalFilterChips
            :model-value="store.filter"
            :counts="store.counts"
            @update:modelValue="onFilterChange"
        />

        <div class="aw-page__columns">
            <div class="aw-page__queue-column">
                <ApprovalQueue
                    :groups="store.groupedByClient"
                    :selected-id="store.selectedId"
                    :loading="store.queueLoading"
                    @select="selectRow"
                />
            </div>
            <div class="aw-page__detail-column">
                <ApprovalDetailPanel
                    :store="store"
                    :can-write="canWrite"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ApprovalSummaryCards from './ApprovalSummaryCards.vue'
import ApprovalFilterChips from './ApprovalFilterChips.vue'
import ApprovalQueue from './ApprovalQueue.vue'
import ApprovalDetailPanel from './ApprovalDetailPanel.vue'
import { useApprovalWorkspaceStore } from '@/stores/approvalWorkspace'

export default {
    name: 'ApprovalWorkspacePage',
    components: {
        ApprovalSummaryCards,
        ApprovalFilterChips,
        ApprovalQueue,
        ApprovalDetailPanel,
    },
    data() {
        return {
            store: useApprovalWorkspaceStore(),
        }
    },
    computed: {
        routeApprovalId() {
            const raw = this.$route?.params?.approvalId
            if (raw == null || raw === '') return null
            const n = Number(raw)
            return Number.isFinite(n) ? n : raw
        },
        canWrite() {
            // The capabilities array is exposed by App.vue via the
            // ``distributorLayoutCapabilities`` injection. Read it here
            // so the detail panel can disable write surfaces for a
            // future read-only role without changing this component's
            // API.
            const caps = this.$root?.$.appContext?.config?.globalProperties
                ?.$distributorCapabilities
            if (Array.isArray(caps)) {
                return caps.includes('distributor.decoration_library.write')
            }
            // When the injection isn't wired, default to assuming
            // write -- the route guard already enforced read, and the
            // backend enforces write at the endpoint boundary.
            return true
        },
    },
    watch: {
        routeApprovalId: {
            immediate: true,
            handler(id) {
                // Push route state into the store. The store fetches
                // detail by id; null clears the right pane.
                this.store.selectApproval(id)
            },
        },
    },
    async mounted() {
        // Fetch queue + clients in parallel; either failing leaves the
        // surface in a degraded-but-readable state (empty queue or
        // anonymous client headers).
        await Promise.allSettled([
            this.store.fetchQueue(),
            this.store.fetchClients(),
        ])
    },
    beforeUnmount() {
        // Don't clear() here -- impersonation stop is the only
        // legitimate clear trigger, and the impersonation store fires
        // that itself. Clearing on every navigation would force a
        // fresh fetch every time a Builder pops back to the workspace.
    },
    methods: {
        onFilterChange(key) {
            this.store.setFilter(key)
        },
        selectRow(id) {
            const current = this.routeApprovalId
            // Idempotent: don't push the same route twice.
            if (current === id) return
            this.$router.push({
                name: 'approval_workspace_detail',
                params: { approvalId: String(id) },
            })
        },
    },
}
</script>

<style scoped>
.aw-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 16px 24px;
}
.aw-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}
.aw-page__title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-page__sub {
    margin: 4px 0 0;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
    max-width: 720px;
}
.aw-page__columns {
    display: grid;
    grid-template-columns: minmax(320px, 380px) 1fr;
    gap: 16px;
    flex: 1;
    min-height: 0;
}
.aw-page__queue-column {
    overflow-y: auto;
    min-height: 0;
}
.aw-page__detail-column {
    display: flex;
    flex-direction: column;
    min-height: 0;
}
</style>
